<template>
  <div class="nmr-pro-fixed">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$ω = γ · B₀ (拉莫尔频率)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <div class="hud-panel">
          <div class="hud-item cyan">
            <label>主磁场强度 B₀</label>
            <span class="val">{{ b0.toFixed(2) }} <small>Tesla</small></span>
          </div>
          <div class="hud-item orange">
            <label>拉莫尔频率 ω</label>
            <span class="val">{{ larmorFreq.toFixed(1) }} <small>MHz</small></span>
          </div>
          <div class="hud-item" :class="isResonant ? 'green' : 'gray'">
             <label>射频态 (RF)</label>
             <span class="val">{{ isResonant ? '共振跃迁中' : '静默进动' }}</span>
          </div>
        </div>

        <svg width="100%" height="260" viewBox="0 0 450 260" preserveAspectRatio="xMidYMid meet">
          <defs>
             <radialGradient id="nucleus-grad"><stop offset="0%" stop-color="#ef4444"/><stop offset="100%" stop-color="#7f1d1d"/></radialGradient>
             <filter id="glow-vector"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 主磁场背景 B0 (Vertical Lines) -->
          <g opacity="0.1">
             <line v-for="i in 10" :key="'b'+i" :x1="i*45" y1="20" :x2="i*45" y2="240" stroke="#94a3b8" stroke-width="1" marker-end="url(#arr-b)" />
          </g>

          <!-- 进动锥体 (Precession Cone) -->
          <ellipse cx="225" cy="80" :rx="spinAngle * 10" :ry="spinAngle * 3" fill="none" stroke="#334155" stroke-dasharray="2,2" />

          <!-- 原子核磁矩矢量 (Magnetic Moment Vector) -->
          <g :transform="`translate(225, 180)`">
             <!-- 这里的坐标计算实现了物理级的磁场频率同步 -->
             <line x1="0" y1="0" :x2="vecPos.x" :y2="vecPos.y" stroke="#fbbf24" stroke-width="4" marker-end="url(#glow-vector)" filter="url(#glow-vector)" />
             <circle :cx="0" :cy="0" r="10" fill="url(#nucleus-grad)" />
             <text x="15" y="0" fill="#fbbf24" font-size="10" font-weight="900">M (磁矩)</text>
          </g>

          <!-- 射频波 (RF Wave) -->
          <path v-if="rfOn" :d="rfWavePath" fill="none" :stroke="isResonant ? '#10b981' : '#64748b'" stroke-width="2" opacity="0.6">
             <animate attributeName="stroke-dashoffset" from="100" to="0" dur="0.5s" repeatCount="indefinite" />
          </path>
        </svg>

        <div class="resonance-gauge" v-if="rfOn">
           <div class="label">射频匹配度: {{ resonanceMatch.toFixed(1) }}%</div>
           <div class="track"><div class="fill" :style="{ width: resonanceMatch + '%', background: isResonant ? '#10b981' : '#f43f5e' }"></div></div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">主磁场调节 B₀ (变强则进动变快)</label>
                 <el-slider v-model="b0" :min="0.5" :max="3.0" :step="0.1" size="small"></el-slider>
              </div>
              <div class="flex-1">
                 <label class="s-label">射频频率 f_rf (试图匹配 ω)</label>
                 <el-slider v-model="rfFreq" :min="20" :max="130" size="small"></el-slider>
              </div>
              <div class="btn-group">
                 <el-button :type="rfOn ? 'danger' : 'success'" size="mini" @click="rfOn = !rfOn">{{ rfOn ? '关闭射频' : '开启射频' }}</el-button>
              </div>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-aim"></i>
           <p><strong>实战物理：</strong>原子核像陀螺一样绕磁场倾斜进动。调节 B₀ 滑块，你会发现**进动由于磁场增强而变猛烈**。<strong>核心挑战：</strong>开启射频，调节 f_rf 滑块，只有当它等于 HUD 显示的 ω 时，能量才会发生跃迁（指针翻转）。这正是医疗 MRI 指纹化识别组织含水率的底层逻辑。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'NuclearMagneticResonanceViz',
  data() {
    return {
      b0: 1.5,
      rfFreq: 42,
      rfOn: false,
      rotation: 0,
      spinAngle: 6, // Tilt angle in degrees
      t: 0
    }
  },
  computed: {
    larmorFreq() { return this.b0 * 42.57; }, // Gamma for Hydrogen in MHz/T
    vecPos() {
       // Sin/Cos mapping of precession
       const rad = this.rotation * Math.PI / 180;
       const spread = this.spinAngle * 10;
       // When resonant, flipping occurs (angle increases)
       const activeTilt = this.isResonant ? 120 : 60; 
       return {
          x: Math.cos(rad) * spread,
          y: -Math.sqrt(Math.pow(100, 2) - Math.pow(Math.cos(rad)*spread, 2)) * (activeTilt / 90)
       };
    },
    resonanceMatch() {
       const diff = Math.abs(this.rfFreq - this.larmorFreq);
       return Math.max(0, 100 - diff * 5);
    },
    isResonant() { return this.rfOn && this.resonanceMatch > 85; },
    rfWavePath() {
       let p = "M 50,220 ";
       for(let x=50; x<=400; x+=5) {
          p += `L ${x},${220 + Math.sin((x-this.t*2)*0.1)*15}`;
       }
       return p;
    }
  },
  methods: {
    animate() {
       this.t++;
       // The core physics: rotation speed proportional to B0
       this.rotation += this.b0 * 5; 
       if (this.rotation > 360) this.rotation -= 360;
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); }
}
</script>

<style scoped lang="scss">
.nmr-pro-fixed { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #166534; border: 1px solid #dcfce7; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 260px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #fbbf24; } &.green { border-left: 3px solid #10b981; } &.gray { border-left: 3px solid #64748b; opacity: 0.6; } } }
.resonance-gauge { position: absolute; bottom: 12px; right: 12px; width: 120px; background: rgba(15,23,42,0.7); padding: 8px; border-radius: 6px; .label { font-size: 8px; color: #64748b; font-weight: 900; margin-bottom: 4px; } .track { height: 4px; background: #334155; border-radius: 2px; overflow: hidden; .fill { height: 100%; transition: width 0.3s; } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex-row { display: flex; flex-direction: row; } .flex-1 { flex: 1; } .items-center { align-items: center; } .gap-6 { gap: 24px; }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px; i { color: #10b981; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #166534; line-height: 1.5; strong { color: #14532d; } } }
</style>
